import FileUpload, {
	splitFileUploads,
	DRIVERS_LICENSE_ACCEPTED_FILE_TYPES,
} from "elements/Input/FileUpload";
import { useEffect, useRef, useState } from "react";
import client from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import Image from "next/image";
import FileUploadSubmittingAnimation from "public/file-upload-submitting-animation.gif";
import FileUploadSuccessAnimation from "public/file-upload-success-animation.gif";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";

const TrustDocumentUpload = () => {
	const [error, setError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const {
		next,
		trustDocumentFilesToUpload,
		setTrustDocumentFilesToUpload,
		specialFieldsValue,
		setSpecialFieldsValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const handleSubmit = async () => {
		if (!details) return;
		setError(false);

		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Trust_Document_Upload_Answer,
			data: {
				"Action Taken": specialFieldsValue?.trust_document_ref
					? "Uploaded"
					: "No Upload",
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});

		try {
			if (trustDocumentFilesToUpload?.length > 0) {
				setSubmitting(true);
				const fd = new FormData();
				fd.append("trustDocument", trustDocumentFilesToUpload[0].file);
				const {
					data: { file_refs },
				} = await client.uploadExemptionFiles({
					formData: fd,
					propertyId: details?.id,
				});
				setSpecialFieldsValue({
					trust_document_ref: file_refs?.trustDocument,
				});
				const trackingEventData = {
					"Action Taken": specialFieldsValue?.trust_document_ref
						? "Uploaded"
						: "No Upload",
				};
				next({ trackingEventData });
			}
		} catch (e) {
			console.error("Exemptions - Error uploading trust document: ", e);
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			sendErrorToWebhook("** Error Uploading Trust Documents ** " + e);
			setSubmitting(false);
			setError(true);
		}
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			{submitting ? (
				<div id="exemptions-file-submitting">
					<Image
						src={FileUploadSubmittingAnimation}
						alt="File upload submitting animation"
					/>
					<p className="mt-2 mb-4">
						Give us a few moments to verify your trust document.
					</p>
					<p className="sm">Please do not close or refresh this page.</p>
				</div>
			) : (
				<>
					<div className="property-profile-question-card exemption-survey-card">
						<div className="property-profile-question-card-title-container">
							<p>Upload proof or certification of Trust.</p>
						</div>
						<p className="exemption-question-card-subtitle sm mb-2">
							Counties will require your Trust documents to verify proof of
							ownership.
						</p>
						<FileUpload
							label="Trust Document"
							numOfFilesAccepted={1}
							accept={DRIVERS_LICENSE_ACCEPTED_FILE_TYPES.join(",")}
							filesToUpload={trustDocumentFilesToUpload}
							updateFilesToUpload={fn => {
								setTrustDocumentFilesToUpload(fn(trustDocumentFilesToUpload));
							}}
						/>
					</div>
				</>
			)}
			{error && (
				<p className="sm text-center terracotta mt-4">
					Whoops, something went wrong. Refresh your page and try again.
				</p>
			)}
			<ButtonV2
				size="large"
				type="submit"
				disabled={
					submitting ||
					!(
						trustDocumentFilesToUpload?.length > 0 ||
						specialFieldsValue?.trust_document_ref
					)
				}
				variant="primary"
				className="exemptions-continue-button">
				{submitting ? "Verifying..." : "Submit Trust Documents"}
			</ButtonV2>
		</form>
	);
};

export const UploadModalSuccess = () => {
	return (
		<div id="property-documents-upload-modal-file-upload-container">
			<div id="property-documents-upload-modal-success-animation-container">
				<Image
					src={FileUploadSuccessAnimation}
					alt="File upload success animation"
					className="property-documents-upload-modal-success-animation"
				/>
			</div>
			<div>
				<p className="lg bold text-center mb-2">
					Your trust document has been uploaded.
				</p>
			</div>
		</div>
	);
};

export default TrustDocumentUpload;
